import { queryOptions, useQuery } from "@tanstack/react-query";

import data from "../../assets/mocks/auctions.json";
import { getAuctionFiltered } from "./utils";

import { env } from "@/config/env";
import { api } from "@/lib/api-client";
import { hasAccessToken } from "@/lib/auth";
import { AxiosMock } from "@/utils/mock";

import type { QueryConfig } from "@/lib/react-query";
import type { Auction } from "@/types/auctions";
import type { PaginatedList } from "@/types/entity";
import type { AuctionQueryParams } from "./types";

if (import.meta.env.MODE === "test") {
  // Mocking the API response
  const mock = AxiosMock.getInstance();
  const uri = new RegExp("^\/carburate\/auctions\/(\\?.*)?$");
  mock.onGet(uri).reply(200, data);
}

export const getAuctions = ({ page, pageSize, status }: AuctionQueryParams) => {
  const query = `?page=${page}&page_size${pageSize}${status ? `&status=${status}` : ""}`;
  return api.get(`/carburate/auctions/${query}`) as Promise<PaginatedList<Auction>>;
};

export const getAuctionsQueryOptions = ({ page, pageSize, status }: AuctionQueryParams = {}) => {
  return queryOptions({
    queryKey: ["auctions", { page, pageSize, status }],
    queryFn: () => getAuctions({ page, pageSize, status }),
  });
};

type UseAuctionsOptions = AuctionQueryParams & {
  queryConfig?: QueryConfig<typeof getAuctions>;
};

export const useAuctions = ({
  page = 1,
  pageSize = 25,
  parameters,
  queryConfig,
  status,
}: UseAuctionsOptions = {}) => {
  const isClientPagination = env.PAGINATION === "client";

  const currentPage = isClientPagination ? 1 : page;
  const currentPageSize = isClientPagination ? 999999 : pageSize;

  let data: PaginatedList<Auction>;

  const {
    data: response,
    isSuccess,
    ...props
  } = useQuery({
    enabled: hasAccessToken(),
    ...getAuctionsQueryOptions({ page: currentPage, pageSize: currentPageSize, status }),
    ...queryConfig,
  });

  data = response!;
  if (isClientPagination && isSuccess) {
    data = getAuctionFiltered(response!, { parameters, page, pageSize });
  }

  return { data, isSuccess, ...props };
};
